import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Mail, Edit, Trash2, UserPlus, Upload, Send, Download, FileSpreadsheet, Archive, RotateCcw, Users, Ban, ShieldOff, Power, AlertTriangle, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import api from '../../utils/api';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active, inactive, all
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [importData, setImportData] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [restoringClient, setRestoringClient] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkSuspendDialogOpen, setBulkSuspendDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    business_type: '',
    send_invitation: false
  });

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients?status=${statusFilter}`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClient = async (clientId, e) => {
    e.stopPropagation();
    setRestoringClient(clientId);
    try {
      await api.post(`/clients/${clientId}/restore`);
      toast.success('Client restored successfully!');
      fetchClients();
    } catch (error) {
      console.error('Failed to restore client:', error);
      toast.error('Failed to restore client');
    } finally {
      setRestoringClient(null);
    }
  };

  const handleReactivateClient = async (clientId, e) => {
    e.stopPropagation();
    setRestoringClient(clientId);
    try {
      await api.post(`/clients/${clientId}/reactivate`);
      toast.success('Client reactivated successfully!');
      fetchClients();
    } catch (error) {
      console.error('Failed to reactivate client:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate client');
    } finally {
      setRestoringClient(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/clients', formData);
      toast.success('Client added successfully!');
      setDialogOpen(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        company_name: '',
        business_type: '',
        send_invitation: false
      });
      setStatusFilter('active'); // Switch to active view to see new client
      fetchClients();
    } catch (error) {
      console.error('Failed to add client:', error);
      toast.error(error.response?.data?.detail || 'Failed to add client');
    }
  };

  const handleSendInvitation = async (clientId) => {
    try {
      await api.post(`/clients/${clientId}/invite`);
      toast.success('Invitation sent successfully!');
      fetchClients();
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const handleBulkImport = async () => {
    try {
      // Parse CSV data
      const lines = importData.trim().split('\n');
      if (lines.length < 2) {
        toast.error('Please provide at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const clientsData = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const clientInfo = {};
        
        headers.forEach((header, idx) => {
          clientInfo[header.replace(/ /g, '_')] = values[idx] || '';
        });
        
        clientsData.push(clientInfo);
      }

      const response = await api.post('/clients/bulk-import', clientsData);
      toast.success(`Imported ${response.data.success} clients successfully!`);
      
      if (response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        toast.warning(`${response.data.failed} clients failed to import`);
      }

      setImportDialogOpen(false);
      setImportData('');
      fetchClients();
    } catch (error) {
      console.error('Failed to import clients:', error);
      toast.error('Failed to import clients');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploadingFile(true);
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        setImportData(content);
        toast.success('CSV file loaded successfully');
      }
      setUploadingFile(false);
    };

    reader.onerror = () => {
      toast.error('Failed to read file');
      setUploadingFile(false);
    };

    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const templateContent = `first_name,last_name,email,company_name,business_type
John,Doe,john.doe@example.com,Acme Corporation,Technology
Jane,Smith,jane.smith@example.com,Smith Consulting,Finance
Robert,Johnson,robert.j@example.com,Johnson & Associates,Legal`;
    
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleBulkInvite = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select clients to invite');
      return;
    }

    try {
      const response = await api.post('/clients/bulk-invite', selectedClients);
      toast.success(`Sent invitations to ${response.data.invited} clients!`);
      
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.failed} invitations failed`);
      }

      setSelectedClients([]);
      fetchClients();
    } catch (error) {
      console.error('Failed to send bulk invitations:', error);
      toast.error('Failed to send invitations');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select clients to archive');
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.post('/clients/bulk-archive', selectedClients);
      toast.success(`Archived ${response.data.archived} clients successfully!`);
      
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.failed} clients could not be archived`);
        console.error('Archive errors:', response.data.errors);
      }

      setSelectedClients([]);
      setBulkArchiveDialogOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Failed to archive clients:', error);
      toast.error('Failed to archive clients');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select clients to delete');
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.post('/clients/bulk-delete', selectedClients);
      toast.success(`Deleted ${response.data.deleted} clients permanently!`);
      
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.failed} clients could not be deleted`);
        console.error('Delete errors:', response.data.errors);
      }

      setSelectedClients([]);
      setBulkDeleteDialogOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Failed to delete clients:', error);
      toast.error('Failed to delete clients');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSuspend = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select clients to suspend');
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.post('/clients/bulk-suspend', selectedClients);
      toast.success(`Suspended ${response.data.suspended} clients successfully!`);
      
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.failed} clients could not be suspended`);
        console.error('Suspend errors:', response.data.errors);
      }

      setSelectedClients([]);
      setBulkSuspendDialogOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Failed to suspend clients:', error);
      toast.error('Failed to suspend clients');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleSelectAll = () => {
    const selectableClients = filteredClients.filter(c => statusFilter === 'active' ? !['archived', 'suspended', 'revoked'].includes(c.status) : true);
    if (selectedClients.length === selectableClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(selectableClients.map(c => c.id));
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const filteredClients = clients.filter(client =>
    client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.business_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count clients by status
  const activeCount = clients.filter(c => c.status === 'active' || !c.status).length;
  const inactiveCount = clients.filter(c => ['archived', 'suspended', 'revoked'].includes(c.status)).length;

  // Get selected client names for confirmation dialogs
  const selectedClientNames = clients
    .filter(c => selectedClients.includes(c.id))
    .map(c => c.company_name || 'Unknown')
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Bulk Action Bar - Shows when clients are selected */}
      {selectedClients.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-teal-600" />
            <span className="font-medium text-teal-800">
              {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {statusFilter === 'active' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkSuspendDialogOpen(true)}
                  className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                  data-testid="bulk-suspend-button"
                >
                  <Ban className="w-4 h-4 mr-1" />
                  Suspend Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkArchiveDialogOpen(true)}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                  data-testid="bulk-archive-button"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  Archive Selected
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedClients([])}
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Archive Dialog */}
      <Dialog open={bulkArchiveDialogOpen} onOpenChange={setBulkArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {selectedClients.length} Client{selectedClients.length > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              This will archive the selected clients and hide them from the main client list. You can restore them later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-2">Selected clients:</p>
            <ul className="text-sm text-gray-800 list-disc list-inside max-h-32 overflow-y-auto">
              {selectedClientNames.map((name, i) => (
                <li key={i}>{name}</li>
              ))}
              {selectedClients.length > 5 && (
                <li className="text-gray-500">...and {selectedClients.length - 5} more</li>
              )}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkArchiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkArchive}
              disabled={bulkActionLoading}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {bulkActionLoading ? 'Archiving...' : 'Archive All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Suspend Dialog */}
      <Dialog open={bulkSuspendDialogOpen} onOpenChange={setBulkSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {selectedClients.length} Client{selectedClients.length > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              This will temporarily block the selected clients from logging into their portal. Their data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-2">Selected clients:</p>
            <ul className="text-sm text-gray-800 list-disc list-inside max-h-32 overflow-y-auto">
              {selectedClientNames.map((name, i) => (
                <li key={i}>{name}</li>
              ))}
              {selectedClients.length > 5 && (
                <li className="text-gray-500">...and {selectedClients.length - 5} more</li>
              )}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSuspendDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkSuspend}
              disabled={bulkActionLoading}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              {bulkActionLoading ? 'Suspending...' : 'Suspend All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete {selectedClients.length} Client{selectedClients.length > 1 ? 's' : ''} Permanently
            </DialogTitle>
            <DialogDescription>
              <span className="text-red-600 font-semibold">Warning: This action cannot be undone!</span>
              <br />
              This will permanently delete all selected clients and their associated data including files, messages, notes, and activity history.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-2">Clients to be deleted:</p>
            <ul className="text-sm text-gray-800 list-disc list-inside max-h-32 overflow-y-auto">
              {selectedClientNames.map((name, i) => (
                <li key={i}>{name}</li>
              ))}
              {selectedClients.length > 5 && (
                <li className="text-gray-500">...and {selectedClients.length - 5} more</li>
              )}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {bulkActionLoading ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="clients-title">Client Management</h2>
          <p className="text-gray-600">Manage your clients and send invitations</p>
        </div>
        <div className="flex gap-2">
          {selectedClients.length > 0 && (
            <Button
              onClick={handleBulkInvite}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Invites ({selectedClients.length})
            </Button>
          )}
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-client-button"
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Client</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      data-testid="client-first-name-input"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      data-testid="client-last-name-input"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="client-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    data-testid="client-company-input"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="business_type">Business Type</Label>
                  <Input
                    id="business_type"
                    data-testid="client-business-type-input"
                    value={formData.business_type}
                    onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="send_invitation"
                    data-testid="send-invitation-checkbox"
                    checked={formData.send_invitation}
                    onChange={(e) => setFormData({ ...formData, send_invitation: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="send_invitation" className="mb-0">Send invitation email</Label>
                </div>
                <Button
                  data-testid="submit-client-button"
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                >
                  Add Client
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter Tabs and Search */}
      <div className="glass rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Status Filter Tabs */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                statusFilter === 'active'
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="filter-active"
            >
              <Users className="w-4 h-4" />
              Active
              {statusFilter !== 'active' && activeCount > 0 && (
                <span className="bg-teal-100 text-teal-700 text-xs px-1.5 py-0.5 rounded-full">{activeCount}</span>
              )}
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                statusFilter === 'inactive'
                  ? 'bg-white text-orange-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="filter-inactive"
            >
              <Ban className="w-4 h-4" />
              Inactive
              {statusFilter !== 'inactive' && inactiveCount > 0 && (
                <span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full">{inactiveCount}</span>
              )}
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                statusFilter === 'all'
                  ? 'bg-white text-gray-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="filter-all"
            >
              All
            </button>
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="search-clients-input"
              type="text"
              placeholder="Search clients by company or business type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="clients-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedClients(filteredClients.map(c => c.id));
                      } else {
                        setSelectedClients([]);
                      }
                    }}
                    className="rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm 
                      ? 'No clients found matching your search' 
                      : statusFilter === 'archived' 
                        ? 'No archived clients' 
                        : 'No clients yet. Add your first client!'
                    }
                  </td>
                </tr>
              ) : (
                filteredClients.map((client, index) => {
                  const isInactive = ['archived', 'suspended', 'revoked'].includes(client.status);
                  const rowBgClass = client.status === 'suspended' ? 'bg-yellow-50/50' 
                    : client.status === 'revoked' ? 'bg-red-50/50' 
                    : client.status === 'archived' ? 'bg-gray-50/50' 
                    : '';
                  
                  return (
                  <tr 
                    key={client.id} 
                    className={`hover:bg-gray-50 transition-colors ${rowBgClass}`}
                    data-testid={`client-row-${index}`}
                  >
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="rounded"
                        disabled={isInactive}
                      />
                    </td>
                    <td 
                      className="px-6 py-4 cursor-pointer" 
                      onClick={() => window.location.href = `/admin/clients/${client.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${isInactive ? 'text-gray-500' : 'text-gray-900'}`}>
                          {client.company_name || 'N/A'}
                        </p>
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                      {client.business_type || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      {client.status === 'archived' ? (
                        <span className="badge bg-gray-100 text-gray-700">Archived</span>
                      ) : client.status === 'suspended' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <Ban className="w-3 h-3" />
                          Suspended
                        </span>
                      ) : client.status === 'revoked' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <ShieldOff className="w-3 h-3" />
                          Revoked
                        </span>
                      ) : (
                        <>
                          <span className="badge badge-success">Active</span>
                          {client.portal_activated ? (
                            <span className="ml-2 text-xs text-green-600 font-medium">(Accepted Invite)</span>
                          ) : client.invitation_sent ? (
                            <span className="ml-2 text-xs text-gray-500">(Invited)</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        {(client.status === 'archived' || client.status === 'suspended' || client.status === 'revoked') ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleReactivateClient(client.id, e)}
                            disabled={restoringClient === client.id}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                            data-testid={`reactivate-client-button-${index}`}
                          >
                            {restoringClient === client.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <>
                                <Power className="w-4 h-4 mr-1" />
                                Reactivate
                              </>
                            )}
                          </Button>
                        ) : (
                          !client.invitation_sent && (
                            <Button
                              data-testid={`send-invite-button-${index}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendInvitation(client.id)}
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Instructions Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV Format Instructions
              </h4>
              <p className="text-sm text-blue-800 mb-2">
                Your CSV file must include the following column headers (first row):
              </p>
              <div className="bg-white rounded p-3 font-mono text-xs text-gray-700 mb-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-bold text-teal-700">first_name</span>
                    <span className="text-gray-500 ml-2">- Required</span>
                  </div>
                  <div>
                    <span className="font-bold text-teal-700">last_name</span>
                    <span className="text-gray-500 ml-2">- Required</span>
                  </div>
                  <div>
                    <span className="font-bold text-teal-700">email</span>
                    <span className="text-gray-500 ml-2">- Required</span>
                  </div>
                  <div>
                    <span className="font-bold text-teal-700">company_name</span>
                    <span className="text-gray-500 ml-2">- Required</span>
                  </div>
                  <div className="col-span-2">
                    <span className="font-bold text-gray-600">business_type</span>
                    <span className="text-gray-500 ml-2">- Optional</span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV Template
              </Button>
            </div>

            {/* Upload CSV File */}
            <div>
              <Label className="mb-2 block">Upload CSV File</Label>
              <div className="flex gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-file-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingFile ? 'Loading...' : 'Choose CSV File'}
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-sm text-gray-500">or paste data below</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* Paste CSV Data */}
            <div>
              <Label className="mb-2 block">Paste CSV Data</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="first_name,last_name,email,company_name,business_type&#10;John,Doe,john@example.com,Acme Corp,Technology&#10;Jane,Smith,jane@example.com,Smith LLC,Finance"
                rows={8}
                className="font-mono text-sm"
                data-testid="import-csv-textarea"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setImportDialogOpen(false);
                  setImportData('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={!importData.trim()}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                data-testid="import-clients-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Clients
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
